import { Component, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { CommonModule } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize } from 'rxjs';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { HolidaysService } from '../../../shared/services/holidays/holidays.service';
import { StorageService } from '../../../shared/services/storage/storage.service';
import { RolesService } from '../../../shared/services/roles/roles.service';

@Component({
  selector: 'app-rule-form',
  imports: [
    CommonModule,
    MatIconModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatSlideToggleModule,
    MatDatepickerModule,
    FormsModule,
    RouterLink,
    LoaderComponent,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './rule-form.component.html',
  styleUrls: ['./rule-form.component.css', '../../../shared/styles/layout.css'],
})
export class RuleFormComponent {
  // Mode management
  isEditMode: boolean = false;
  ruleId: number | null = null;

  // Common properties
  myroles: any = {};
  ruleForm: FormGroup;
  rule: any = {};
  isSubmitting = false;
  attemptedSubmit = false;
  returnLink: string = '/home/vacations/protocols';

  private _snackBar = inject(MatSnackBar);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private holidaysService: HolidaysService,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private loaderService: LoaderService,
    private storageService: StorageService,
    private rolesService: RolesService
  ) {
    this.ruleForm = this.fb.group({
      name: ['', [Validators.required]],
      value: [null, [Validators.min(0)]],
      date_range_start: [null],
      date_range_end: [null],
      boolean_value: [false],
    }, { validators: this.dateRangeValidator });
  }

  // Custom validator to ensure both dates are filled or both are empty, and end >= start
  private dateRangeValidator(control: AbstractControl): ValidationErrors | null {
    const startDate = control.get('date_range_start')?.value;
    const endDate = control.get('date_range_end')?.value;

    // Both empty is valid
    if (!startDate && !endDate) {
      return null;
    }

    // One filled and one empty is invalid
    if (!startDate || !endDate) {
      return { dateRangeIncomplete: true };
    }

    // Both filled - check that end >= start
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (end < start) {
      return { dateRangeInvalid: true };
    }

    return null;
  }

  get isLoading() {
    return this.loaderService.getLoader();
  }

  ngOnInit() {
    this.route.params.subscribe((params) => {
      if (params['id']) {
        this.isEditMode = true;
        this.ruleId = +params['id'];
        this.loaderService.showLoader();
        this.loadRuleData();
      } else {
        this.isEditMode = false;
        this.ruleId = null;
      }
    });
    this.loadRoles();
  }

  loadRoles() {
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'Vacaciones',
      this.storageService
    );

    if (permissions) {
      this.myroles = permissions;

      const permissionMessage = this.isEditMode
        ? 'No tienes permisos para editar una regla'
        : 'No tienes permisos para añadir una regla';

      this.rolesService.checkWritePermission(
        'Vacaciones',
        this.storageService,
        this._snackBar,
        this.router,
        this.returnLink,
        permissionMessage
      );
    }
  }

  private loadRuleData(): void {
    if (!this.ruleId) return;

    this.holidaysService
      .getRuleById(this.ruleId)
      .pipe(
        finalize(() => {
          this.loaderService.hideLoader();
        })
      )
      .subscribe({
        next: (data) => {
          this.rule = data;
          this.ruleForm.patchValue({
            name: data.name,
            value: data.value,
            date_range_start: data.date_range_start,
            date_range_end: data.date_range_end,
            boolean_value: data.boolean_value,
          });
        },
        error: (error) => {
          console.error('Error loading rule:', error);
          this.snackBar.open(
            'Error al cargar los datos de la regla',
            'Cerrar',
            { duration: 3000 }
          );
          this.router.navigate([this.returnLink], { queryParams: { tab: 'reglas' } });
        },
      });
  }

  saveChanges(): void {
    this.loaderService.showLoader();
    if (this.isSubmitting) return;
    this.attemptedSubmit = true;

    if (!this.ruleForm.valid) {
      this.loaderService.hideLoader();
      // Mark all controls as touched to trigger validation visuals
      Object.keys(this.ruleForm.controls).forEach((key) => {
        const control = this.ruleForm.get(key);
        control?.markAsTouched();
      });

      let errorMessage = 'Por favor completa todos los campos requeridos';
      if (this.ruleForm.hasError('dateRangeIncomplete')) {
        errorMessage = 'Si completas una fecha, debes completar ambas fechas del rango';
      } else if (this.ruleForm.hasError('dateRangeInvalid')) {
        errorMessage = 'La fecha de fin debe ser mayor o igual a la fecha de inicio';
      } else if (this.ruleForm.get('value')?.hasError('min')) {
        errorMessage = 'El valor no puede ser negativo';
      }

      this.snackBar.open(errorMessage, 'Cerrar', { duration: 5000 });
      return;
    }

    this.isSubmitting = true;

    if (this.isEditMode) {
      this.updateRule();
    } else {
      this.createRule();
    }
  }

  private createRule(): void {
    const formValue = this.ruleForm.value;

    const body = {
      name: formValue.name,
      value: formValue.value,
      date_range_start: this.formatDate(formValue.date_range_start),
      date_range_end: this.formatDate(formValue.date_range_end),
      boolean_value: formValue.boolean_value,
    };

    // TODO: Implement postRule in HolidaysService
    this.loaderService.hideLoader();
    this.isSubmitting = false;
    this.snackBar.open(
      'Funcionalidad de creación pendiente de implementación',
      'Cerrar',
      { duration: 3000 }
    );
    console.log('Rule to create:', body);
  }

  private formatDate(date: any): string | null {
    if (!date) return null;
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private updateRule(): void {
    if (!this.ruleId) return;

    const formValue = this.ruleForm.value;

    const body = {
      name: formValue.name,
      value: formValue.value,
      date_range_start: this.formatDate(formValue.date_range_start),
      date_range_end: this.formatDate(formValue.date_range_end),
      boolean_value: formValue.boolean_value,
    };

    this.holidaysService
      .updateRule(this.ruleId, body)
      .pipe(
        finalize(() => {
          this.loaderService.hideLoader();
          this.isSubmitting = false;
        })
      )
      .subscribe({
        next: (response) => {
          this.snackBar.open('Regla actualizada correctamente', 'Cerrar', {
            duration: 3000,
          });
          this.router.navigate([this.returnLink], { queryParams: { tab: 'reglas' } });
        },
        error: (error) => {
          console.error('Error updating rule:', error);
          this.snackBar.open('Error al actualizar la regla', 'Cerrar', {
            duration: 3000,
          });
        },
      });
  }

  cancel(): void {
    this.router.navigate([this.returnLink], { queryParams: { tab: 'reglas' } });
  }
}
